// Email to Todolix on the web (the iPhone's MailViews.swift): the address of the Inbox, an area or a project, made,
// copied, replaced or removed; and Settings' list of them all. Making one is part of Pro, as in the apps. "Improve
// with AI" stays in the apps, where AI is agreed to (docs/47).

import { useState } from "react";
import { addressFor, emailOf, type MailKind } from "../data/mail";
import { t } from "../i18n";
import { useWindow } from "./context";
import { splitEmoji } from "./format";
import { Icon } from "./Icon";
import { Dialog } from "./Sheets";

function footer(kind: MailKind, label: string): string {
  return kind === "inbox"
    ? t("Mail to this address becomes a to-do in your Inbox: the subject is the title, the text the notes. Forward mails to it, or save it as a contact.")
    : t("Mail to this address becomes a to-do in {name}. Renaming or moving it does not change the address. If it is ever completed or deleted, mail goes to the Inbox instead.", { name: label });
}

/** Copies the text; a browser that refuses leaves it selected instead. */
async function copy(text: string, field: HTMLElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    if (field) getSelection()?.selectAllChildren(field);
    return false;
  }
}

export function MailAddressSheet({ kind, targetID, label, onClose }: { kind: MailKind; targetID?: string; label: string; onClose: () => void }) {
  const { state, model, showSheet } = useWindow();
  const address = addressFor(state.mailAddresses, kind, targetID);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [asksRenew, setAsksRenew] = useState(false);
  const [copied, setCopied] = useState(false);
  const [field, setField] = useState<HTMLElement | null>(null);
  const run = async (work: () => Promise<unknown>) => {
    setWorking(true);
    setProblem(null);
    try {
      await work();
    } catch {
      setProblem(t("That did not work. Please try again."));
    }
    setWorking(false);
  };
  const name = splitEmoji(label).name || label;
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{kind === "inbox" ? t("Email to Inbox") : t("Email to {name}", { name })}</h2>
      {address ? (
        <>
          <div className="mail-address">
            <code ref={setField} className="mono">{emailOf(address)}</code>
            <button className="outline-button small" disabled={working}
              onClick={() => void copy(emailOf(address), field).then(setCopied)}>
              <Icon name={copied ? "check" : "templates"} />{copied ? t("Copied") : t("Copy Address")}
            </button>
          </div>
          <p className="sheet-note">{footer(kind, name)}</p>
          {asksRenew ? (
            <div className="confirm-inline">
              <p><b>{t("Replace this address?")}</b> {t("Mail to the old address will no longer arrive.")}</p>
              <div className="actions">
                <button className="outline-button" onClick={() => setAsksRenew(false)}>{t("Cancel")}</button>
                <button className="mint-button" disabled={working}
                  onClick={() => void run(async () => { await model.mail.renew(address, label); setAsksRenew(false); setCopied(false); })}>
                  {t("New Address")}
                </button>
              </div>
            </div>
          ) : (
            <div className="mail-tools">
              <button className="outline-button small" disabled={working} onClick={() => setAsksRenew(true)}>{t("New Address")}</button>
              <button className="outline-button small destructive" disabled={working} onClick={() => void run(() => model.mail.remove(address))}>{t("Remove Address")}</button>
              <p className="sheet-note">{t("If the address got around, a new one replaces it. The old one stops working at once.")}</p>
            </div>
          )}
        </>
      ) : (
        <>
          <p>{footer(kind, name)}</p>
          <div className="mail-tools">
            <button className="mint-button" disabled={working}
              onClick={() => (state.isPro ? void run(() => model.mail.create(kind, targetID, label)) : showSheet({ kind: "pro", feature: "mail" }))}>
              {t("Create Address")}
            </button>
          </div>
        </>
      )}
      {problem && <p className="sheet-problem" role="alert">{problem}</p>}
      <div className="actions"><button className="mint-button" onClick={onClose}>{t("Done")}</button></div>
    </Dialog>
  );
}

/** Settings › Email to Todolix: the Inbox's address and every other one, with where it leads. */
export function MailSettings() {
  const { state, showSheet } = useWindow();
  const inbox = addressFor(state.mailAddresses, "inbox");
  const others = state.mailAddresses.filter((address) => address.kind !== "inbox");
  // The current name, which may differ from the one the address was made with.
  const nameOf = (kind: MailKind, targetID: string | undefined, label: string) =>
    (kind === "project" ? state.lists.project(targetID)?.title : kind === "area" ? state.lists.area(targetID)?.title : undefined) ?? label;
  const row = (kind: MailKind, targetID: string | undefined, label: string, email: string | undefined) => (
    <button key={`${kind}-${targetID ?? ""}`} className="settings-row mail-row" onClick={() => showSheet({ kind: "mail", mailKind: kind, targetID, label })}>
      <div><b>{label}</b><span className={email ? "mono" : undefined}>{email ?? t("No address yet")}</span></div>
      <Icon name="chevRight" />
    </button>
  );
  return (
    <section className="settings-group" aria-label={t("Email to Todolix")}>
      <h3>{t("Email to Todolix")}</h3>
      {row("inbox", undefined, t("Inbox"), inbox ? emailOf(inbox) : undefined)}
      <p className="settings-note">{t("One address for everything, into the Inbox.")}</p>
      <h4 className="settings-subhead">{t("Areas and Projects")}</h4>
      {others.map((address) => row(address.kind, address.targetID, nameOf(address.kind, address.targetID, address.label), emailOf(address)))}
      <p className="settings-note">{others.length === 0
        ? t("An area or project gets its own address from its menu: Email Address.")
        : t("Each goes straight to its area or project.")}</p>
    </section>
  );
}
